/** Pure seek/scrub math shared by bars and waveform progress tracks. */

export interface MediaPlayerSegmentBounds {
	startPercent: number;
	endPercent: number;
}

/** Width of the interactive scrub hit target along the full track (0–100). */
export function resolveMediaPlayerScrubHitWidthPercent(
	maxSeekPercent: number,
	continuationPercent?: number,
): number {
	const maxSeek = Math.max(0, Math.min(100, maxSeekPercent));
	const continuation =
		typeof continuationPercent === "number"
			? Math.max(0, Math.min(100, continuationPercent))
			: maxSeek;
	return Math.max(maxSeek, continuation);
}

/** Start of the locked (non-scrub) zone for cursor + hit testing (0–100). */
export function resolveMediaPlayerContinuationLockStartPercent(
	maxSeekPercent: number,
	furthestConsumedPercent: number,
	continuationPercent?: number,
): number {
	return resolveMediaPlayerScrubClampPercent(
		maxSeekPercent,
		continuationPercent,
		furthestConsumedPercent,
	);
}

/**
 * Max global % the user may drag/commit to. Never clamped per-segment.
 * Unlock frontier = widest of max-seek, furthest consumed, and continuation fill.
 * (Continuation alone must not shrink below maxSeek — that made previewer bars
 * look fully `cursor-not-allowed` when maxSeek was 100 but continuation was 0.)
 */
export function resolveMediaPlayerScrubClampPercent(
	maxSeekPercent: number,
	continuationPercent?: number,
	furthestConsumedPercent?: number,
): number {
	const maxSeek = Math.max(0, Math.min(100, maxSeekPercent));
	const furthest = Math.max(
		0,
		Math.min(100, furthestConsumedPercent ?? maxSeek),
	);
	const continuation =
		typeof continuationPercent === "number"
			? Math.max(0, Math.min(100, continuationPercent))
			: 0;
	return Math.max(maxSeek, furthest, continuation);
}

export function resolvePointerPercentOnTrack(
	clientX: number,
	element: HTMLElement,
): number {
	const bounds = element.getBoundingClientRect();
	if (bounds.width <= 0) {
		return 0;
	}
	return Math.max(
		0,
		Math.min(100, ((clientX - bounds.left) / bounds.width) * 100),
	);
}

/** 0–100 fill within one segment region given global playhead/scrub percent. */
export function resolveSegmentPlayheadFillPercent(
	segment: MediaPlayerSegmentBounds,
	globalPlayheadPercent: number,
): number {
	if (globalPlayheadPercent >= segment.endPercent) {
		return 100;
	}
	if (globalPlayheadPercent <= segment.startPercent) {
		return 0;
	}
	const span = segment.endPercent - segment.startPercent;
	if (span <= 0) {
		return 0;
	}
	return ((globalPlayheadPercent - segment.startPercent) / span) * 100;
}
